import { BatchUpdateRequest, BatchUpdateResponse, MemberUpdate, PermanentInjury, SoldierDTO, SpellcasterDTO } from '../types';
import { persistCurrentState, loadWarbandData, buildHydrationMaps } from './localStorageService';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const getRefMaps = () => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
  const s = useReferenceDataStore.getState();
  return buildHydrationMaps({
    weapons: s.weapons,
    armours: s.armours,
    buffs: s.buffs,
    treasureItems: s.treasureItems,
    baseResources: s.allBaseResources,
    injuries: s.injuries,
  });
};

function applyMemberUpdate<T extends SpellcasterDTO | SoldierDTO>(member: T, update: MemberUpdate, injuryMap: Map<string, PermanentInjury>): T {
  return {
    ...member,
    ...(update.currentHealth !== undefined && { currentHealth: update.currentHealth }),
    ...(update.carryingTreasure !== undefined && { carryingTreasure: update.carryingTreasure }),
    ...(update.leftTable !== undefined && { leftTable: update.leftTable }),
    ...(update.name !== undefined && { name: update.name }),
    ...(update.notes !== undefined && { notes: update.notes }),
    ...(update.badlyWounded !== undefined && { badlyWounded: update.badlyWounded }),
    ...(update.permanentInjuryIds !== undefined && {
      permanentInjuries: update.permanentInjuryIds
        .map((id) => injuryMap.get(id))
        .filter((i): i is PermanentInjury => i !== undefined),
    }),
  };
}

/**
 * Applies the request updates to the warbandStore, persists to localStorage,
 * and returns a BatchUpdateResponse reflecting the updated state.
 */
export const batchUpdateService = {
  batchUpdate: async (warbandId: string, request: BatchUpdateRequest): Promise<{ data: BatchUpdateResponse }> => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useWarbandStore } = require('../stores/warbandStore') as typeof import('../stores/warbandStore');
    const storeState = useWarbandStore.getState();
    const maps = getRefMaps();

    // Apply request member updates to the store so that persistCurrentState
    // and the response both reflect the requested changes.
    if (request.wizard && storeState.wizardData) {
      storeState.setWizardData(applyMemberUpdate(storeState.wizardData, request.wizard, maps.injuryMap));
    }
    if (request.apprentice && storeState.apprenticeData) {
      storeState.setApprenticeData(applyMemberUpdate(storeState.apprenticeData, request.apprentice, maps.injuryMap));
    }
    if (request.soldiers && storeState.soldiersData) {
      const updateMap = new Map(request.soldiers.map((u) => [u.id, u]));
      storeState.setSoldiersData({
        soldiers: storeState.soldiersData.soldiers.map((s) => {
          const upd = updateMap.get(s.id);
          return upd ? applyMemberUpdate(s, upd, maps.injuryMap) : s;
        }),
      });
    }
    if (request.warbandBuffIds !== undefined) {
      const countById = new Map<string, number>();
      for (const id of request.warbandBuffIds) countById.set(id, (countById.get(id) ?? 0) + 1);
      const newBuffs = Array.from(countById.entries()).map(([id, count]) => {
        const buff = maps.buffMap.get(id);
        return buff ? { ...buff, stackCount: count } : null;
      }).filter((b): b is NonNullable<typeof b> => b !== null);
      storeState.setWarbandBuffs(newBuffs);
    }

    persistCurrentState(warbandId);

    const stored = loadWarbandData(warbandId);
    const state = useWarbandStore.getState();

    const effectiveBuffIds = request.warbandBuffIds ?? stored?.warbandBuffIds ?? [];
    const warbandBuffs = (() => {
      const countById = new Map<string, number>();
      for (const id of effectiveBuffIds) countById.set(id, (countById.get(id) ?? 0) + 1);
      return Array.from(countById.entries()).map(([id, count]) => {
        const buff = maps.buffMap.get(id);
        return buff ? { ...buff, stackCount: count } : null;
      }).filter((b): b is NonNullable<typeof b> => b !== null);
    })();

    const baseResources = (stored?.selectedBaseResourceIds ?? [])
      .map((id) => maps.baseResourceMap.get(id))
      .filter((r): r is NonNullable<typeof r> => r !== null && r !== undefined);

    const response: BatchUpdateResponse = {
      wizard: state.wizardData,
      apprentice: state.apprenticeData,
      soldiers: state.soldiersData,
      warbandSpells: state.spellcastersAndSpells?.warbandSpells ?? [],
      vaultItems: state.vaultItems,
      baseId: stored?.warband.baseId ?? null,
      baseResources,
      warbandBuffs,
      warband: stored?.warband ?? null,
    };

    return { data: response };
  },
};
